import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Collapse,
  Empty,
  Popconfirm,
  Progress,
  Space,
  Tooltip,
  message,
} from 'antd';
import {
  CalendarOutlined,
  CaretLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  UserOutlined,
} from '@ant-design/icons';
import WorkFilePanel from '../shared/WorkFilePanel';
import { subTaskFilesApi, subTasksApi, tasksApi } from '../../../utils/workApi';
import { toJalali } from '../../../utils/dateUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { diffNewFiles } from '../../../company/features';

const fmtDate = (v) => (v ? toJalali(v) || v : null);
const toFaDigits = (value) =>
  String(value ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const SubTaskPanelHeader = ({
  folderIndex,
  subtask,
  done,
  onToggleStatus,
  fileCount,
  statusLoading = false,
}) => {
  const due = fmtDate(subtask.due_date || subtask.due_on);
  const start = fmtDate(subtask.start_date || subtask.start_on);
  const folderTitle = `پوشه ${toFaDigits(folderIndex)}`;

  return (
    <div className="flex items-center gap-3 w-full min-w-0 pr-1" style={{ fontFamily: 'CustomFont' }}>
      <Tooltip title={done ? 'علامت به‌عنوان ناتمام' : 'علامت به‌عنوان انجام‌شده'}>
        <button
          type="button"
          disabled={statusLoading}
          className="shrink-0 bg-transparent border-0 p-0 cursor-pointer leading-none disabled:opacity-50"
          onClick={async (e) => {
            e.stopPropagation();
            await onToggleStatus(!done);
          }}
          aria-label={done ? 'تغییر به ناتمام' : 'تغییر به انجام‌شده'}
        >
          {done ? (
            <CheckCircleFilled style={{ color: '#22c55e', fontSize: 20 }} />
          ) : (
            <CloseCircleFilled style={{ color: '#ef4444', fontSize: 20 }} />
          )}
        </button>
      </Tooltip>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
          {folderTitle}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-[11px] text-gray-500">
          {start ? (
            <span className="inline-flex items-center gap-1">
              <CalendarOutlined />
              شروع: {start}
            </span>
          ) : null}
          {due ? (
            <span className="inline-flex items-center gap-1">
              <CalendarOutlined />
              سررسید: {due}
            </span>
          ) : null}
          {fileCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <PaperClipOutlined />
              {fileCount} فایل
            </span>
          ) : null}
        </div>
      </div>
      {subtask.assigned_to?.name ? (
        <Tooltip title={subtask.assigned_to.name}>
          <Avatar
            size={28}
            src={subtask.assigned_to.image_url || subtask.assigned_to.image}
            icon={<UserOutlined />}
            onClick={(e) => e.stopPropagation()}
          >
            {subtask.assigned_to.name?.[0]}
          </Avatar>
        </Tooltip>
      ) : null}
    </div>
  );
};

const SubTaskAccordion = ({
  subtasks = [],
  taskId,
  onRefresh,
  onAddClick,
  addLoading = false,
  askNeedConvert = false,
}) => {
  const [activeKey, setActiveKey] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [statusBusyId, setStatusBusyId] = useState(null);

  const doneCount = subtasks.filter(
    (s) => s.status === 'complete' || s.status === 'completed',
  ).length;
  const subProgress = subtasks.length ? Math.round((doneCount / subtasks.length) * 100) : 0;
  const totalFiles = subtasks.reduce(
    (sum, s) => sum + (Array.isArray(s.files) ? s.files.length : 0),
    0,
  );

  const refreshStay = async () => {
    await onRefresh?.();
  };

  if (!subtasks.length) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <Button
            type="primary"
            className={PANEL_PRIMARY_BTN_CLASS}
            loading={addLoading}
            onClick={onAddClick}
          >
            افزودن زیروظیفه
          </Button>
        </div>
        <Empty description={<span style={{ fontFamily: 'CustomFont' }}>زیروظیفه‌ای ثبت نشده</span>} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            پیشرفت زیروظیفه‌ها ({doneCount}/{subtasks.length})
          </span>
          <Space wrap size="small">
            {totalFiles > 0 ? (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={async () => {
                  try {
                    await subTaskFilesApi.downloadAllSubtasks(taskId);
                  } catch {
                    message.error('خطا در دانلود فایل‌ها');
                  }
                }}
              >
                دانلود همه فایل‌های زیروظایف
              </Button>
            ) : null}
            <Button
              type="primary"
              size="small"
              className={PANEL_PRIMARY_BTN_CLASS}
              loading={addLoading}
              onClick={onAddClick}
            >
              افزودن زیروظیفه
            </Button>
          </Space>
        </div>
        <Progress percent={subProgress} size="small" style={{ fontFamily: 'CustomFont' }} />
      </div>

      <Collapse
        accordion
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key || null)}
        expandIcon={({ isActive }) => (
          <CaretLeftOutlined
            rotate={isActive ? -90 : 0}
            className="text-gray-400"
            style={{ fontSize: 12 }}
          />
        )}
        className="subtask-accordion bg-transparent border-0"
        items={subtasks.map((s, index) => {
          const done = s.status === 'complete' || s.status === 'completed';
          const files = Array.isArray(s.files) ? s.files : [];
          const key = String(s.id);

          return {
            key,
            className:
              'mb-2 !rounded-xl !border !border-gray-200 dark:!border-gray-700 !overflow-hidden bg-white dark:bg-gray-800/80 shadow-sm',
            style: { marginBottom: 8 },
            label: (
              <SubTaskPanelHeader
                folderIndex={index + 1}
                subtask={s}
                done={done}
                fileCount={files.length}
                statusLoading={statusBusyId === s.id}
                onToggleStatus={async (nextDone) => {
                  setStatusBusyId(s.id);
                  try {
                    await subTasksApi.changeStatus(
                      s.id,
                      nextDone ? 'complete' : 'incomplete',
                    );
                    await refreshStay();
                  } catch {
                    message.error('خطا در تغییر وضعیت زیروظیفه');
                  } finally {
                    setStatusBusyId(null);
                  }
                }}
              />
            ),
            extra: (
              <Popconfirm
                title="حذف زیروظیفه؟"
                onConfirm={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await subTasksApi.delete(s.id);
                    message.success('زیروظیفه حذف شد');
                    await refreshStay();
                  } catch {
                    message.error('خطا در حذف');
                  }
                }}
                onCancel={(e) => e?.stopPropagation?.()}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            ),
            children: (
              <div className="space-y-4 pt-1">
                {s.description ? (
                  String(s.description).includes('<') ? (
                    <div
                      className="text-sm text-gray-700 dark:text-gray-200 leading-7 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: s.description }}
                    />
                  ) : (
                    <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-7">
                      {s.description}
                    </div>
                  )
                ) : (
                  <div className="text-xs text-gray-400">توضیحاتی ثبت نشده</div>
                )}

                <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-600 p-3 bg-gray-50/80 dark:bg-gray-900/30">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 inline-flex items-center gap-1.5">
                      <PaperClipOutlined />
                      فایل‌های زیروظیفه
                      {files.length ? ` (${files.length})` : ''}
                    </div>
                    {files.length > 0 ? (
                      <Space size="small" wrap>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={busyId === `dl-${s.id}`}
                          onClick={async () => {
                            setBusyId(`dl-${s.id}`);
                            try {
                              await subTaskFilesApi.downloadAll(s.id);
                            } catch {
                              message.error('خطا در دانلود');
                            } finally {
                              setBusyId(null);
                            }
                          }}
                        >
                          دانلود همه
                        </Button>
                        <Popconfirm
                          title="حذف همه فایل‌های این زیروظیفه؟"
                          onConfirm={async () => {
                            setBusyId(`del-${s.id}`);
                            try {
                              await subTaskFilesApi.deleteAll(s.id);
                              message.success('فایل‌ها حذف شدند');
                              await refreshStay();
                            } catch {
                              message.error('خطا در حذف فایل‌ها');
                            } finally {
                              setBusyId(null);
                            }
                          }}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={busyId === `del-${s.id}`}
                          >
                            حذف همه
                          </Button>
                        </Popconfirm>
                      </Space>
                    ) : null}
                  </div>
                  <WorkFilePanel
                    files={files}
                    onUpload={async (fl, opts) => {
                      const beforeFiles = Array.isArray(files) ? [...files] : [];
                      const res = await subTaskFilesApi.upload(s.id, fl, opts);
                      const loadAfterFiles = async () => {
                        const fresh = await tasksApi.getById(taskId);
                        const t = fresh?.task || fresh?.data?.task || null;
                        const subs = t?.subtasks || t?.sub_tasks || [];
                        const sub = subs.find((x) => String(x.id) === String(s.id));
                        return sub?.files || [];
                      };
                      let afterFiles = [];
                      try {
                        afterFiles = await loadAfterFiles();
                        if (opts?.needConvert && !diffNewFiles(beforeFiles, afterFiles, fl.length).length) {
                          await new Promise((r) => setTimeout(r, 800));
                          afterFiles = await loadAfterFiles();
                        }
                      } catch {
                        /* ignore */
                      }
                      await refreshStay();
                      const newFiles = diffNewFiles(beforeFiles, afterFiles, fl.length);
                      return {
                        ...(res && typeof res === 'object' ? res : {}),
                        __newFiles: newFiles,
                        __afterFiles: afterFiles,
                        files: newFiles.length ? newFiles : res?.files || [],
                      };
                    }}
                    onDelete={(fid) => subTaskFilesApi.delete(fid).then(refreshStay)}
                    getDownloadUrl={subTaskFilesApi.downloadUrl}
                    uploadLabel="آپلود فایل زیروظیفه"
                    askNeedConvert={askNeedConvert}
                    getConvertProgress={(fileId) => subTaskFilesApi.getConvertProgress(fileId)}
                    onConvertComplete={refreshStay}
                  />
                </div>
              </div>
            ),
          };
        })}
      />

      <style>{`
        .subtask-accordion .ant-collapse-header {
          align-items: center !important;
          padding: 12px 14px !important;
        }
        .subtask-accordion .ant-collapse-content-box {
          padding: 8px 16px 16px !important;
        }
        .subtask-accordion > .ant-collapse-item {
          border-radius: 12px !important;
        }
      `}</style>
    </div>
  );
};

export default SubTaskAccordion;
